import React, { useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Textarea } from "../ui/textarea";
import { Loader2 } from "lucide-react";

export function FeedbackPopover({ children, align = "end", side = "top" }) {
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("general");
  const [selectedMood, setSelectedMood] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const moodOptions = useMemo(
    () => [
      { id: "frustrated", emoji: "😡", label: "Frustrated" },
      { id: "unhappy", emoji: "😟", label: "Not great" },
      { id: "neutral", emoji: "😐", label: "It’s okay" },
      { id: "happy", emoji: "🙂", label: "Happy" },
      { id: "delighted", emoji: "😍", label: "Loving it" },
    ],
    []
  );

  const feedbackTypes = useMemo(
    () => ({
      general: "General feedback",
      bug: "Bug report",
      idea: "Product idea",
      praise: "Love note",
    }),
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
          console.error("No token found");
          return;
      }

      const metadataFields = {
        "Platform feedback": feedback,
        ...(selectedMood ? { "Feedback sentiment": selectedMood.label } : {}),
        ...(feedbackType
          ? { "Feedback category": feedbackTypes[feedbackType] }
          : {}),
      };

      await axios.patch(
        "/api/candidates/updateCandidate",
        {
          fields: metadataFields,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setIsSuccess(true);
      setFeedback("");
      setSelectedMood(null);
      setFeedbackType("general");
      setTimeout(() => {
        setIsSuccess(false);
        setIsOpen(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to send feedback:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverAnchor asChild>
        <div className="pointer-events-none fixed bottom-8 left-28" />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side="top"
        sideOffset={16}
        className="w-[min(95vw,420px)] rounded-3xl border border-slate-100 bg-white/95 p-6 text-slate-900 shadow-2xl backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      >
        <div className="grid gap-4">
          {isSuccess ? (
            <div className="flex flex-col items-start justify-center py-8 text-left text-green-600">
              <p className="text-base font-semibold">Thanks for sharing!</p>
              <p className="text-sm text-slate-500">
                We’ll review your feedback and follow up if needed.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="flex items-center justify-between text-xs font-medium text-slate-500 uppercase tracking-wide">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="transition hover:text-slate-900"
                >
                  Cancel
                </button>
                <span className="text-sm normal-case text-slate-900">
                  Give feedback
                </span>
                <button
                  type="submit"
                  disabled={isLoading || !feedback.trim()}
                  className="text-green-600 disabled:text-slate-300"
                >
                  Submit
                </button>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium leading-none">
                  Share feedback or report a bug
                </h4>
                <p className="text-sm text-slate-500">
                  Tell us about your experience, what’s working, or any bugs you
                  spotted.
                </p>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                {moodOptions.map((mood) => {
                  const isActive = selectedMood?.id === mood.id;
                  return (
                    <button
                      key={mood.id}
                      type="button"
                      onClick={() => setSelectedMood(mood)}
                      className={`flex size-10 items-center justify-center rounded-full text-xl transition ${
                        isActive
                          ? "bg-green-100 ring-2 ring-green-400"
                          : "hover:bg-slate-100"
                      }`}
                      aria-label={mood.label}
                    >
                      {mood.emoji}
                    </button>
                  );
                })}
              </div>

              <div className="grid gap-1">
                <label htmlFor="feedbackType" className="text-sm font-medium">
                  What type of feedback is this?
                </label>
                <select
                  id="feedbackType"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                >
                  {Object.entries(feedbackTypes).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-2">
                <label htmlFor="feedback" className="text-sm font-medium">
                  Feedback
                </label>
                <Textarea
                  id="feedback"
                  placeholder="Give us the details—screens, steps to reproduce, or ideas for improvements."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="min-h-[120px]"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isLoading || !feedback.trim()}
                  size="sm"
                  className="gap-2"
                >
                  {isLoading && (
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  )}
                  Send feedback
                </Button>
              </div>
            </form>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}